import { useState } from "react";
import { useUser } from "canbook-data/user/user-provider";
import { useRouter } from "next/router";
import { DbUser } from "canbook-data/user/models";
import { useBusiness } from "canbook-data/business/provider";
import {
  OtpComponentCallback,
  PhoneComponentCallback,
  ProfileComponentCallback,
} from "./hooks";
import { useIsMobile } from "./effects/useIsMobile";
import { useInviteTokenHandler } from "./effects/useInviteTokenHandler";
import { useAuthRedirect } from "./effects/useAuthRedirect";
import { useBusinessRedirect } from "./effects/useBusinessRedirect";

export const usePageState = () => {
  const router = useRouter();
  const { user, setUser, userLoading } = useUser();
  const { loadingBusinesses, businesses } = useBusiness();
  const queryParams = router.query;
  const redirectUrl = queryParams["redirect-url"];
  const minSmallerResolution = 1024;

  const [isLoginMethodShow, setIsLoginMethodShow] = useState<boolean>(true);
  const [isPhoneShow, setIsPhoneShow] = useState<boolean>(false);
  const [isEmailShow, setIsEmailShow] = useState<boolean>(false);
  const [isOtpShow, setIsOtpShow] = useState<boolean>(false);
  const [isProfileShow, setIsProfileShow] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>("");
  const [phone, setPhone] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [verificationId, setVerificationId] = useState<string>("");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const isMobile = useIsMobile(minSmallerResolution);

  const inviteTokenParam = router.query["invite_token"];
  useInviteTokenHandler({
    router,
    inviteTokenParam,
    setPhone,
    setIsLoginMethodShow,
    setIsPhoneShow,
    setIsOtpShow,
    setIsProfileShow,
  });

  useAuthRedirect({
    router,
    user,
    userLoading,
    queryParams,
    redirectUrl,
    setCheckingAuth,
  });

  useBusinessRedirect({
    router,
    loadingBusinesses,
    businesses,
  });

  const showPhoneScreen = () => {
    setIsLoginMethodShow(false);
    setIsPhoneShow(true);
    setIsEmailShow(false);
    setIsOtpShow(false);
    setIsProfileShow(false);
  };

  const showEmailScreen = () => {
    setIsLoginMethodShow(false);
    setIsPhoneShow(false);
    setIsEmailShow(true);
    setIsOtpShow(false);
    setIsProfileShow(false);
  };

  const loginMethodCallback = {
    onSelectPhone: showPhoneScreen,
    onSelectEmail: showEmailScreen,
  };

  const phoneCallback: PhoneComponentCallback = {
    onSuccess: (verificationId: string, phone: string) => {
      setPhone(phone);
      setEmail("");
      setVerificationId(verificationId);
      setIsPhoneShow(false);
      setIsEmailShow(false);
      setIsOtpShow(true);
    },
  };

  const emailCallback = {
    onSuccess: (verificationId: string, email: string) => {
      setEmail(email);
      setPhone("");
      setVerificationId(verificationId);
      setIsPhoneShow(false);
      setIsEmailShow(false);
      setIsOtpShow(true);
    },
  };

  const otpCallback: OtpComponentCallback = {
    onSuccess: async (user) => {
      if (!user.user_name || user.user_name == "") {
        setUserId(user.id);
        setIsOtpShow(false);
        setIsProfileShow(true);
      } else {
        setTimeout(() => {
          if (queryParams && redirectUrl) {
            router.replace(redirectUrl as string);
          }
        }, 50);
      }
    },
    onEditPhone: () => {
      setIsOtpShow(false);
      setIsPhoneShow(true);
      setIsEmailShow(false);
    },
    onEditEmail: () => {
      setIsOtpShow(false);
      setIsEmailShow(true);
      setIsPhoneShow(false);
    },
  };

  const profileCallback: ProfileComponentCallback = {
    onSuccess: async (user: DbUser) => {
      setUser(user);
      setIsPhoneShow(false);
      setIsOtpShow(false);
      if (!isMobile) {
        router.replace("/");
      } else {
        setIsProfileShow(false);
      }
    },
  };

  return {
    isLoginMethodShow,
    isPhoneShow,
    isEmailShow,
    isOtpShow,
    isProfileShow,
    loginMethodCallback,
    phoneCallback,
    emailCallback,
    otpCallback,
    profileCallback,
    userId,
    phone,
    email,
    verificationId,
    checkingAuth,
    showPhoneScreen,
    showEmailScreen,
  };
};
